import { useEffect, useState } from 'react';

import { useLocation } from 'react-router';

import { useTranslation } from 'react-i18next';

import { extractGameUrl } from '@/utils/document-utils';
import { itemByLanguage } from '@/utils/language';

import { TrainingPlan } from '@/components/document';
import NoMessage from '@/components/NoMessage/NoMessage';

import Constants from '@/constants';

import { LanguageObject } from '@/types/types';

import { DocumentDto } from '@/_generated/api-clients/xp';
import {
  PlanResource,
  PlanResourceReference,
  Step,
} from '@/_generated/api-clients/xp';
import { useAmplitudeTrack } from '@/amplitude/useAmplitude';

interface Document extends Omit<DocumentDto, 'references'> {
  references: Record<
    string,
    {
      name: string;
      versionId: string;
      fileId: string;
      reference?: string;
      url?: string;
      sourceSystem:
        | typeof Constants.DOCUMENT_TYPES.TRIDION
        | typeof Constants.DOCUMENT_TYPES.XYLEME
        | typeof Constants.DOCUMENT_TYPES.GAME
        | typeof Constants.DOCUMENT_TYPES.VIDEO;
    }
  >;
}

interface NewDocProxyState {
  planId: string;
  step?: Step & { resource: PlanResource };
  isCompleted: boolean;
  userIds: string[];
  stepStatus: string;
}

export interface DocumentProxyState {
  planId: string;
  step?: {
    id: string;
    name: LanguageObject;
    type: string;
    quizId: string | null;
    reference?: Document;
    resource?: PlanResource;
    note: string | null;
    urls: string[];
    available: boolean;
  };
  isCompleted: boolean;
  userIds: string[];
  stepStatus: string;
}

const DocumentProxy = () => {
  const track = useAmplitudeTrack();
  const { state } = useLocation() as {
    state?: DocumentProxyState | NewDocProxyState;
  };

  const { t } = useTranslation();
  const [gameUrl, setGameUrl] = useState<string>();
  const [isLoading, setIsLoading] = useState(true);

  // Type guard to check if state uses new structure (PlanResource)
  const isNewDocProxyState = (s: typeof state): s is NewDocProxyState =>
    !!(s?.step && 'resource' in s.step);

  // a checklist has an id - we call it planId or checklistId
  // within a checklist there are steps, which have an id, stepId
  // steps contain fields like: notes, quizId, urls...
  // within a step there are references that have an id, referenceId
  // referenceId ultimately refers to a training document

  const isNewState = isNewDocProxyState(state);

  let documentItem:
    | PlanResourceReference
    | Document['references'][string]
    | undefined;
  let reference: PlanResourceReference | Document | undefined;
  let refType: string | undefined;
  let referenceId: string | undefined;
  let contentUrl: string | undefined;

  if (isNewState) {
    reference = state.step?.resource.references?.[0];
    refType = state.step?.resource.type ?? undefined;
    referenceId = state.step?.resource.id ?? undefined;
    documentItem = reference; // PlanResourceReference
    contentUrl = documentItem?.urls.at(0)?.contentUrl;
  } else {
    reference = state?.step?.reference;
    refType =
      typeof state?.step?.reference === 'object'
        ? state.step.reference.type
        : undefined;
    referenceId =
      reference && typeof reference === 'object' ? reference.id : undefined;
    documentItem =
      reference && typeof reference === 'object' && 'references' in reference
        ? itemByLanguage(reference.references)
        : undefined;
    contentUrl = documentItem?.url;
  }

  const planId = state?.planId;
  const stepId = state?.step?.id;
  const stepStatus = state?.stepStatus;
  const userIds = state?.userIds;
  const isGame =
    refType?.toLocaleLowerCase() ===
    Constants.DOCUMENT_TYPES.GAME.toLocaleLowerCase();

  useEffect(() => {
    if (!isGame || !contentUrl) {
      return;
    }
    (async () => {
      const htmlUrl = await extractGameUrl(contentUrl);
      setGameUrl(htmlUrl);
      setIsLoading(false);
    })();
  }, [isGame, contentUrl]);

  useEffect(() => {
    if (!track || !referenceId) {
      return;
    }

    const resourceName = documentItem?.name;

    if (!resourceName) {
      return;
    }

    track('ResourceView', {
      resourceId: referenceId,
      resourceName,
    });
  }, [documentItem, referenceId, track, isNewState]);

  if (isGame && contentUrl && isLoading) {
    return null;
  }

  const planWrapperClasses = `flex w-full ${
    documentItem?.sourceSystem.toLocaleLowerCase() ===
    Constants.DOCUMENT_TYPES.TRIDION.toLowerCase()
      ? 'max-w-[1152px]'
      : 'max-w-[840px]'
  }`;

  return referenceId ? (
    <div className={`pt-8 ${planWrapperClasses}`}>
      {planId && stepId && stepStatus && (
        <TrainingPlan
          planId={planId}
          refFileId={
            isNewState
              ? undefined
              : (documentItem as Document['references'][string] | undefined)
                  ?.fileId
          }
          refUrl={
            isNewState
              ? // Game url is always the more explicit url, for any state
                (gameUrl ?? contentUrl)
              : (gameUrl ??
                (documentItem as Document['references'][string] | undefined)
                  ?.reference ??
                contentUrl)
          }
          refVersionId={
            isNewState
              ? undefined
              : (documentItem as Document['references'][string] | undefined)
                  ?.versionId
          }
          stepId={stepId}
          stepStatus={stepStatus}
          userIds={userIds}
          name={
            isNewState
              ? documentItem?.name
              : (documentItem as Document['references'][string] | undefined)
                  ?.name
          }
          isTridion={
            (isNewState
              ? (documentItem as PlanResourceReference | undefined)
                  ?.sourceSystem
              : (documentItem as Document['references'][string] | undefined)
                  ?.sourceSystem
            )?.toLocaleLowerCase() ===
            Constants.DOCUMENT_TYPES.TRIDION.toLowerCase()
          }
          // @TODO: The documentItem is not typed correctly and should be derived from the generated client.
          lastModified={
            documentItem &&
            typeof documentItem === 'object' &&
            'updatedAt' in documentItem &&
            typeof documentItem.updatedAt === 'string'
              ? documentItem.updatedAt
              : undefined
          }
        />
      )}
    </div>
  ) : (
    <NoMessage message={t('TrainingPlans.noResourceReference')} />
  );
};

export default DocumentProxy;
